import {
  createUserWithEmailAndPassword,
  deleteUser,
  sendEmailVerification as fbSendEmailVerification,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateEmail,
  updatePassword as fbUpdatePassword,
  User as FirebaseUser
} from 'firebase/auth';
import {
  doc,
  getDoc,
  increment,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import type { Dispatch } from 'redux';
import { auth, db } from './init';
import * as ActionTypes from '../constants';
import type { UserDoc } from './schema';
import { saveUserCache, clearUserCache } from '../userCache';
import { AR_SETTINGS_DEFAULTS } from '../../common/types/arSettings';

/** Default editor preferences for a new user (mirrors the client preferences reducer initialState). */
export const DEFAULT_PREFERENCES = {
  fontSize: 18,
  autosave: true,
  linewrap: true,
  lineNumbers: true,
  lintWarning: false,
  textOutput: false,
  gridOutput: false,
  theme: 'light',
  autorefresh: false,
  language: 'en-US',
  autocloseBracketsQuotes: true,
  autocompleteHinter: false,
  arSettings: { ...AR_SETTINGS_DEFAULTS }
} as const;

/**
 * Create a Firebase Auth user, then atomically create the `users/{uid}` profile and the
 * `usernames/{username}` uniqueness mirror. If the username is already taken, the transaction aborts
 * and the just-created auth user is rolled back (deleted) so the email can be reused.
 * Sends a verification email on success.
 */
export async function signUp(
  username: string,
  email: string,
  password: string
): Promise<void> {
  const credential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );
  const { uid } = credential.user;

  try {
    await runTransaction(db, async (tx) => {
      const usernameRef = doc(db, `usernames/${username}`);
      const existing = await tx.get(usernameRef);
      if (existing.exists()) {
        // Tag with a code so the UI can show a localized message (the text is a fallback only).
        throw Object.assign(
          new Error(`Username "${username}" is already taken.`),
          { code: 'app/username-taken' }
        );
      }
      tx.set(usernameRef, { uid });
      tx.set(doc(db, `users/${uid}`), {
        username,
        email,
        preferences: { ...DEFAULT_PREFERENCES },
        totalSize: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
  } catch (error) {
    // Roll back the orphaned auth user so the email is free to retry.
    if (auth.currentUser) {
      await deleteUser(auth.currentUser).catch(() => {});
    }
    throw error;
  }

  await fbSendEmailVerification(credential.user).catch(() => {});
}

/**
 * Sign in with email + password. Throws on bad credentials, and — because Firebase happily signs in an
 * unverified account — also rejects (after signing back out) when the email is not yet verified, so the
 * caller can show a clear "verify your email first" message instead of a silent broken session.
 */
export async function logIn(email: string, password: string): Promise<void> {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  if (!credential.user.emailVerified) {
    await fbSignOut(auth);
    const error = new Error(
      'Please verify your email before logging in.'
    ) as Error & { code: string };
    error.code = 'auth/email-not-verified';
    throw error;
  }
}

/** Sign the current user out. */
export async function logOut(): Promise<void> {
  await fbSignOut(auth);
}

/** Send a password-reset email (Firebase Auth built-in). */
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

/** (Re)send a verification email to the currently signed-in user. */
export async function sendVerificationEmail(): Promise<void> {
  if (!auth.currentUser) throw new Error('No authenticated user.');
  await fbSendEmailVerification(auth.currentUser);
}

/** Read the Firestore profile doc for a uid. Returns null if missing. */
export async function loadUserDoc(uid: string): Promise<UserDoc | null> {
  const snap = await getDoc(doc(db, `users/${uid}`));
  return snap.exists() ? (snap.data() as UserDoc) : null;
}

/**
 * Assemble the Redux `user` object from the Firebase auth user + the Firestore profile doc. `role`
 * comes from the ID-token custom claim ('instructor' | 'admin'); absent claim → null (plain student).
 */
export function buildReduxUser(
  firebaseUser: FirebaseUser,
  userDoc: UserDoc,
  role: string | null = null
) {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email ?? userDoc.email,
    username: userDoc.username,
    preferences: userDoc.preferences,
    totalSize: userDoc.totalSize,
    // Denormalized current-classroom pointer (name copied because students can't read classrooms/*).
    currentClassroomId: userDoc.currentClassroomId ?? '',
    currentClassroomName: userDoc.currentClassroomName ?? '',
    verified: firebaseUser.emailVerified ? 'verified' : 'unverified',
    role,
    authenticated: true as const
  };
}

/** Read the `role` custom claim from a Firebase user's ID token. Returns null when unclaimed. */
async function readRoleClaim(
  firebaseUser: FirebaseUser
): Promise<string | null> {
  const result = await firebaseUser.getIdTokenResult();
  const { role } = result.claims;
  return role === 'instructor' || role === 'admin' ? role : null;
}

/**
 * Subscribe to Firebase auth-state changes and mirror them into Redux. On sign-in, loads the profile
 * doc and dispatches AUTH_USER + SET_PREFERENCES; on sign-out, dispatches UNAUTH_USER.
 * Returns the unsubscribe function. Call once at app boot.
 */
export function subscribeToAuthChanges(dispatch: Dispatch) {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      clearUserCache();
      dispatch({ type: ActionTypes.UNAUTH_USER });
      return;
    }
    // auth.currentUser now exists, so authorized reads work — flip the gate immediately (before the
    // slower profile read below) so data fetches can start ~1 round-trip earlier on a cold load.
    dispatch({ type: ActionTypes.AUTH_READY });
    const userDoc = await loadUserDoc(firebaseUser.uid);
    const role = await readRoleClaim(firebaseUser);
    // Bail if auth state changed while we were awaiting the profile read — prevents a stale
    // callback from overwriting a newer auth state (onAuthStateChanged does not serialize async).
    if (auth.currentUser?.uid !== firebaseUser.uid) return;
    if (!userDoc) {
      // Auth user with no profile (interrupted signup, or a transient read failure). Treat as
      // signed out, but log it so a transient Firestore error isn't silently a logout.
      // eslint-disable-next-line no-console
      console.warn(
        `subscribeToAuthChanges: no profile doc for uid ${firebaseUser.uid}.`
      );
      clearUserCache();
      dispatch({ type: ActionTypes.UNAUTH_USER });
      return;
    }
    const reduxUser = buildReduxUser(firebaseUser, userDoc, role);
    dispatch({ type: ActionTypes.AUTH_USER, user: reduxUser });
    dispatch({
      type: ActionTypes.SET_PREFERENCES,
      preferences: reduxUser.preferences
    });
    // Snapshot for the next cold load so the real user + preferences render immediately (the reducer
    // sets authenticated + authHydrated true on AUTH_USER, so mirror that in the cache).
    saveUserCache({
      user: { ...reduxUser, authenticated: true, authHydrated: true },
      preferences: reduxUser.preferences
    });
  });
}

/**
 * Re-read the current Firebase user (and force a fresh ID token) and mirror it back into Redux.
 * Call this when a user returns to the app after clicking the email-verification link: their open
 * session's cached emailVerified flag (and token claim) is stale until refreshed, so the "Unconfirmed"
 * banner would otherwise persist — and a save would still be blocked by the stale token — until a full
 * reload/re-login. No-op when signed out.
 */
export async function refreshAuthUser(dispatch: Dispatch): Promise<void> {
  const current = auth.currentUser;
  if (!current) return;
  await current.reload();
  await current.getIdToken(true);
  const refreshed = auth.currentUser;
  if (!refreshed) return;
  const userDoc = await loadUserDoc(refreshed.uid);
  if (!userDoc) return;
  // The forced getIdToken(true) above also refreshes custom claims, so a newly-granted role is
  // picked up here (the same path a promoted user hits after re-login / email verification).
  const role = await readRoleClaim(refreshed);
  dispatch({
    type: ActionTypes.AUTH_USER,
    user: buildReduxUser(refreshed, userDoc, role)
  });
}

/**
 * Update the current user's account. Email and password are updated on Firebase Auth; email is also
 * written to the profile doc. Username is fixed at signup and cannot be changed here.
 *
 * Caveats (Firebase Auth, production): `updateEmail`/`updatePassword` throw
 * `auth/requires-recent-login` when the session is stale — the caller (UI) must catch this and
 * prompt re-authentication. Also, if `updateEmail` succeeds but the profile `setDoc` fails, Auth
 * (the source of truth for email) and the profile doc diverge until the next successful write;
 * reads tolerate this via `buildReduxUser`'s `firebaseUser.email ?? userDoc.email` fallback.
 */
export async function updateUserAccount(changes: {
  email?: string;
  newPassword?: string;
}): Promise<void> {
  const current = auth.currentUser;
  if (!current) throw new Error('No authenticated user.');

  // Apply password FIRST. Both updateEmail and updatePassword require a recent login; doing password
  // first means a stale session throws BEFORE the email is committed to Auth+Firestore, avoiding a
  // partial change where the email silently persisted but the user was told nothing changed.
  if (changes.newPassword) {
    await fbUpdatePassword(current, changes.newPassword);
  }
  if (changes.email && changes.email !== current.email) {
    await updateEmail(current, changes.email);
    await setDoc(
      doc(db, `users/${current.uid}`),
      { email: changes.email, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
}

/**
 * Persist a partial set of editor preferences to the current user's profile doc, so per-preference
 * changes (font size, theme, autosave, …) survive across sessions. Firestore `merge` deep-merges the
 * nested `preferences` map, so only the changed keys are written. No-op when signed out. This replaces
 * the dead Express `PUT /preferences` endpoint that the Firebase migration left behind.
 */
export async function saveUserPreferences(
  preferences: Record<string, unknown>
): Promise<void> {
  const current = auth.currentUser;
  if (!current) return;
  await setDoc(
    doc(db, `users/${current.uid}`),
    { preferences, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Persist a change in cumulative media usage (bytes) to users/{uid}.totalSize via an atomic
 * increment, so the per-user upload quota survives across sessions instead of resetting to 0 each
 * reload. Pass a positive delta on upload/clone. No-op when signed out. NOTE: this is a SOFT quota —
 * totalSize is owner-writable, so it deters accidental overuse but a determined client could tamper;
 * hard enforcement would need a Cloud Function / Storage trigger.
 */
export async function incrementUserTotalSize(
  deltaBytes: number
): Promise<void> {
  const current = auth.currentUser;
  if (!current || !deltaBytes) return;
  await updateDoc(doc(db, `users/${current.uid}`), {
    totalSize: increment(deltaBytes),
    updatedAt: serverTimestamp()
  });
}
